<script lang="ts" module>
  export type ProgressTone = 'brand' | 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'auto';
</script>

<script lang="ts">
  // Horizontal progress / usage bar.
  //   <ProgressBar value={hours} max={budget} tone="auto" label="Budget" showValue />
  //   <ProgressBar value={week} max={goal} tone="auto" mode="goal" size="xs" />
  //   <ProgressBar value={share} color={clientColor(c)} size="sm" />
  // tone="auto":
  //   mode "usage" (budgets, default): below thresholds.warning → brand,
  //                                    from warning → warning, above danger (default 1 = over budget) → danger
  //   mode "goal"  (targets):          below 100% → brand, reached → success
  // Values above max fill the bar and show an overflow edge.
  import '../messages/ui';
  import { t } from '../i18n.svelte';
  import { formatPercent } from '../format';

  interface Props {
    value: number | null | undefined;
    max?: number;
    tone?: ProgressTone;
    mode?: 'usage' | 'goal';
    /** Ratios (0–1+) where tone="auto" switches to warning / danger. */
    thresholds?: { warning?: number; danger?: number };
    /** Custom fill colour (overrides tone), e.g. a client colour. */
    color?: string;
    size?: 'xs' | 'sm' | 'md' | 'lg';
    /** Visible label above the bar (also the accessible name). */
    label?: string;
    /** Accessible name when there is no visible label. */
    ariaLabel?: string;
    /** Show the value right of the label: true = percentage, string = your text. */
    showValue?: boolean | string;
    class?: string;
  }
  let { value, max = 100, tone = 'brand', mode = 'usage', thresholds, color, size = 'sm', label, ariaLabel, showValue = false, class: cls = '' }: Props = $props();

  const v = $derived(Math.max(0, Number(value) || 0));
  const ratio = $derived(max > 0 ? v / max : 0);
  const pct = $derived(Math.min(100, ratio * 100));
  const over = $derived(ratio > 1);
  const resolved = $derived.by(() => {
    if (tone !== 'auto') return tone;
    if (mode === 'goal') return ratio >= 1 ? 'success' : 'brand';
    const warn = thresholds?.warning ?? 0.8;
    const danger = thresholds?.danger ?? 1;
    return ratio > danger ? 'danger' : ratio >= warn ? 'warning' : 'brand';
  });
  const valueText = $derived(typeof showValue === 'string' ? showValue : formatPercent(ratio * 100));
</script>

<div class="pb {size} {cls}">
  {#if label || showValue}
    <div class="head">
      {#if label}<span class="label">{label}</span>{/if}
      {#if showValue}<span class="val tabular tone-{resolved}">{valueText}</span>{/if}
    </div>
  {/if}
  <div
    class="track"
    role="progressbar"
    aria-label={label || ariaLabel || t('ui.progress')}
    aria-valuemin={0}
    aria-valuemax={max}
    aria-valuenow={Math.round(v * 100) / 100}
    aria-valuetext={valueText}
  >
    <div class="fill tone-{resolved}" class:over style:width="{pct}%" style:background={color}></div>
  </div>
</div>

<style>
  .pb { --h: 6px; display: flex; flex-direction: column; gap: var(--space-1_5); min-width: 0; }
  .xs { --h: 4px; }
  .sm { --h: 6px; }
  .md { --h: 8px; }
  .lg { --h: 12px; }
  .head { display: flex; align-items: baseline; justify-content: space-between; gap: var(--space-2); font-size: var(--text-xs); }
  .label { color: var(--text-2); font-weight: var(--weight-medium); min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .val { font-weight: var(--weight-semibold); color: var(--text); flex-shrink: 0; }
  .track { height: var(--h); border-radius: var(--radius-full); background: var(--surface-3); overflow: hidden; }
  .fill { height: 100%; border-radius: inherit; background: var(--brand); transition: width var(--dur-slow) var(--ease), background var(--dur); }
  .fill.over { box-shadow: inset -3px 0 0 color-mix(in srgb, var(--text) 25%, transparent); }
  .fill.tone-brand { background: var(--brand); }
  .fill.tone-success { background: var(--success); }
  .fill.tone-warning { background: var(--warning); }
  .fill.tone-danger { background: var(--danger); }
  .fill.tone-info { background: var(--info); }
  .fill.tone-neutral { background: var(--text-3); }
  .val.tone-warning { color: var(--warning-strong); }
  .val.tone-danger { color: var(--danger-strong); }
  .val.tone-success { color: var(--success-strong); }
</style>
